import { useState } from "react";
import ReuseModal from "../../../../../components/fragment/modal/ReuseModal";
import RazorInputField from "../../../../../components/razor-reuse/inputField/RazorInputField";
import {
  RazorCheckBox,
  RazorToggleSlide,
} from "../../../../../components/razor-reuse";
import RootState from "../../../../../redux/type";
import { useDispatch, useSelector } from "react-redux";
import { reactSelectStyleTable } from "../../../../../utils/Helper";
import {
  createNewVendor,
  getAllThirdPartyVendors,
  getAllVendors,
} from "../../../../../redux/thirdParty";
import { VENDOR_CATEGORY_TABS } from "../vendorControlHelpers";

interface myComponentProps {
  onClose?: () => void;
  visible?: boolean;
}

interface Formvalue {
  vendor_name: { label: string; value: string } | null;
  category: { label: string; value: string } | null;
  applies_to_mobile: boolean;
  applies_to_web: boolean;
  mobile_is_active: boolean;
  web_is_active: boolean;
  status: string;
  has_wallet: boolean;
  supported_currencies: { label: string; value: string }[];
}

const EMPTY_FORM: Formvalue = {
  vendor_name: null,
  category: null,
  applies_to_mobile: false,
  applies_to_web: false,
  mobile_is_active: false,
  web_is_active: false,
  status: "inactive",
  has_wallet: false,
  supported_currencies: [],
};

// has_wallet/supported_currencies only make sense for vendors that actually
// move or hold currency — exchange-rate-only vendors have neither.
const CATEGORIES_WITH_WALLET_FIELDS = ["wallet", "transaction"];

const CreateVendorModal = ({ onClose, visible }: myComponentProps) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState<Formvalue>(EMPTY_FORM);

  const { vendors, updating_vendors } = useSelector(
    (state: RootState) => state.thirdParty
  );
  const { all_currencies_drop } = useSelector(
    (state: RootState) => state.settings
  );

  const vendorOptions =
    vendors?.map((chi: string) => ({
      label: chi?.toUpperCase(),
      value: chi,
    })) || [];

  const currencyOptions =
    (all_currencies_drop || [])
      .filter((chi: any) => chi?.code)
      .map((chi: any) => ({
        label: chi.code?.toUpperCase(),
        value: chi.code?.toLowerCase(),
      })) || [];

  const showWalletFields = CATEGORIES_WITH_WALLET_FIELDS.includes(
    form.category?.value || ""
  );

  const handleClose = () => {
    onClose?.();
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async () => {
    const payload = {
      ...form,
      vendor_name: form?.vendor_name?.value,
      // category is optional — omit it entirely rather than send null when unset.
      category: form?.category?.value || undefined,
      // has_wallet/supported_currencies are only relevant (and shown) for
      // wallet/transaction vendors — omit them otherwise rather than send
      // stale values for a category they don't apply to.
      has_wallet: showWalletFields ? form.has_wallet : undefined,
      supported_currencies: showWalletFields
        ? form.supported_currencies?.map((chi) => chi.value)
        : undefined,
    };
    const data = await dispatch(createNewVendor(payload) as any);

    if (data?.payload?.data?.status) {
      setForm(EMPTY_FORM);
      dispatch(getAllVendors() as any);
      dispatch(getAllThirdPartyVendors() as any);
      handleClose();
    }
  };

  return (
    <>
      <ReuseModal
        title="Add New Vendor"
        btnLabel="Add Vendor"
        visible={visible}
        onClose={handleClose}
        disable={!form.vendor_name || updating_vendors}
        loading={updating_vendors}
        onBtnClick={handleSubmit}
      >
        <div className="-mt-[20px]">
          <RazorInputField
            value={form.vendor_name}
            selectOption={vendorOptions}
            selectStyles={reactSelectStyleTable}
            type={`select`}
            className={`select-class`}
            name="vendor_name"
            onChange={(e: any) => {
              setForm((prev) => ({
                ...prev,
                vendor_name: e,
              }));
            }}
            color="black-light"
            label="Vendor Name"
            placeholder="Enter Vendor Name"
          />

          <RazorInputField
            value={form.category}
            selectOption={VENDOR_CATEGORY_TABS}
            selectStyles={reactSelectStyleTable}
            type={`select`}
            className={`select-class mt-[14px]`}
            name="category"
            onChange={(e: any) => {
              setForm((prev) => ({
                ...prev,
                category: e,
              }));
            }}
            color="black-light"
            label="Category (optional)"
            placeholder="Select Category"
            isClearable
          />

          {showWalletFields && (
            <>
              <RazorInputField
                value={form.supported_currencies}
                selectOption={currencyOptions}
                selectStyles={reactSelectStyleTable}
                type={`select`}
                className={`select-class mt-[14px]`}
                name="supported_currencies"
                multi
                onChange={(e: any) => {
                  setForm((prev) => ({
                    ...prev,
                    supported_currencies: e || [],
                  }));
                }}
                color="black-light"
                label="Supported Currencies (optional)"
                placeholder="Select Currencies"
              />

              <figure className="flex items-center gap-[6px] mt-[14px]">
                <RazorCheckBox
                  checked={form.has_wallet}
                  onChange={(e) => {
                    setForm((prev) => ({
                      ...prev,
                      has_wallet: e.target.checked,
                    }));
                  }}
                  color={`black-light`}
                  id={`has_wallet` as any}
                  key={`has_wallet` as any}
                />
                <p className="text-[#1e1e1e] text-[13px]">
                  Vendor Has Wallet (optional)
                </p>
              </figure>
            </>
          )}

          <div className="mt-[14px]">
            <p className="text-[#596080] text-[13px] mb-[8px]">
              Service Support
            </p>

            <figure className="flex items-center gap-[6px]">
              <RazorCheckBox
                checked={form.applies_to_mobile}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    applies_to_mobile: e.target.checked,
                  }));
                }}
                color={`black-light`}
                id={`applies_to_mobile` as any}
                key={`applies_to_mobile` as any}
              />
              <p className="text-[#1e1e1e] text-[13px]">Service for Mobile</p>
            </figure>
            <figure className="flex items-center gap-[6px] mt-[10px]">
              <RazorCheckBox
                checked={form.applies_to_web}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    applies_to_web: e.target.checked,
                  }));
                }}
                color={`black-light`}
                id={`applies_to_web` as any}
                key={`applies_to_web` as any}
              />
              <p className="text-[#1e1e1e] text-[13px]">Service for Web</p>
            </figure>
          </div>

          <div className="mt-[14px]">
            <p className="text-[#596080] text-[13px] mb-[8px]">Status</p>

            <figure className="flex justify-between items-center gap-[6px]">
              <p className="text-[#1e1e1e] text-[14px]">Mobile</p>
              <RazorToggleSlide
                checked={form.mobile_is_active}
                onChange={(e: any) => {
                  setForm((prev) => ({
                    ...prev,
                    mobile_is_active: e.target.checked,
                  }));
                }}
                color={`black-light`}
                id={`mobile_is_active` as any}
              />
            </figure>
            <figure className="flex justify-between items-center gap-[6px] mt-[10px]">
              <p className="text-[#1e1e1e] text-[14px]">Web</p>
              <RazorToggleSlide
                checked={form.web_is_active}
                onChange={(e: any) => {
                  setForm((prev) => ({
                    ...prev,
                    web_is_active: e.target.checked,
                  }));
                }}
                color={`black-light`}
                id={`web_is_active` as any}
              />
            </figure>
          </div>

          <figure className="inline-flex items-center gap-[6px] mt-[20px]">
            <RazorToggleSlide
              checked={form.status === "active"}
              onChange={(e: any) => {
                setForm((prev) => ({
                  ...prev,
                  status: e.target.checked ? "active" : "inactive",
                }));
              }}
              color={`black-light`}
              id={`status` as any}
            />
            <p className="text-[#1e1e1e] text-[14px]">Activate Vendor?</p>
          </figure>

          <div className="mt-[14px] rounded-[8px] bg-[#DEA0220D] text-[#C78F1F] text-[12px] py-[14px] px-[12px] border-[1px] border-[#C78F1F]">
            Note: Only one vendor can be active per service type. Activating
            this vendor will automatically deactivate others.
          </div>
        </div>
      </ReuseModal>
    </>
  );
};

export default CreateVendorModal;
